// utils/formatters.ts

export const formatDate = (dateInput: string | Date | null | undefined): string => {
    if (!dateInput) return 'N/A';

    const date = new Date(dateInput);
    if (isNaN(date.getTime())) {
        return 'Invalid Date';
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();

    return `${day} ${month} ${year}`;
};

/**
 * Human "time since" for a past timestamp — "today", "yesterday",
 * "N days ago", or the absolute date once it is a fortnight or more old.
 * Returns '' for null/invalid input so callers can omit the line entirely.
 */
/**
 * How long ago, in the largest whole unit: "today", "yesterday", "5 days ago", "3 weeks ago",
 * "3 months ago", "1 year ago". Through Intl.RelativeTimeFormat, so the words follow CLDR. For a
 * check whose age matters (the site tags' website check); formatRelativeDate falls back to the
 * date itself after a fortnight, which hid that a check was three months old.
 */
export const formatAge = (dateInput: string | Date | null | undefined, now: Date = new Date()): string => {
    if (!dateInput) return '';
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return '';
    const days = Math.floor((now.getTime() - date.getTime()) / 86_400_000);
    const rtf = new Intl.RelativeTimeFormat('en-NZ', { numeric: 'auto' });
    if (days < 14) return rtf.format(-Math.max(days, 0), 'day');
    if (days < 60) return rtf.format(-Math.floor(days / 7), 'week');
    if (days < 365) return rtf.format(-Math.floor(days / 30), 'month');
    return rtf.format(-Math.floor(days / 365), 'year');
};

/** Age in whole days, for thresholds */
export const ageInDays = (dateInput: string | Date | null | undefined, now: Date = new Date()): number | null => {
    if (!dateInput) return null;
    const date = new Date(dateInput);
    return isNaN(date.getTime()) ? null : Math.floor((now.getTime() - date.getTime()) / 86_400_000);
};

export const formatRelativeDate = (dateInput: string | Date | null | undefined): string => {
    if (!dateInput) return '';

    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return '';

    const days = Math.floor((Date.now() - date.getTime()) / 86_400_000);
    if (days < 0) return formatDate(date);
    if (days === 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 14) return `${days} days ago`;
    return formatDate(date);
};

export const formatCompactNumber = (number: number | null | undefined): string => {
    if (number === null || number === undefined) return '0';

    const formatter = Intl.NumberFormat('en', {
        notation: 'compact',
        maximumFractionDigits: 1,
    });

    return formatter.format(number);
};

// ── Currency formatting ───────────────────────────────────────────────────
//
// The backend emits raw numeric currency fields paired with an ISO 4217
// `currency_code` (see project_currency_formatting_*.md in shared memory).
// Intl.NumberFormat handles every ISO 4217 currency natively — correct
// symbols, correct fraction digits (including zero-decimal JPY and
// three-decimal KWD), and locale-aware separators. We trust it and don't
// maintain a parallel table.
//
// A missing code is a bug upstream, not a formatting choice — but rendering a
// bare number is worse than rendering it with the wrong symbol, so there is one
// fallback and exactly one. NZD, because this is a NZ-first product. Previously
// `formatCurrency` fell back to AUD and `formatCompactCurrency` to USD, so the
// same figure could render as A$1,200 in a table and US$1.2K in the tile above
// it. Always pass an explicit code where the data carries one.

/** The single fallback currency. See the note above before changing it. */
export const FALLBACK_CURRENCY = 'NZD';

/**
 * Format a numeric currency value using Intl.NumberFormat.
 * Returns an em dash for null/undefined/empty/non-finite input.
 *
 * @param value    the amount (number). Strings are coerced for legacy
 *                 callers but should not be relied on — the contract is
 *                 a raw number.
 * @param code     ISO 4217 currency code. Defaults to NZD — see the note above
 *                 on why there is a single fallback rather than one per function.
 * @param locale   optional locale override; defaults to the browser's.
 * @param options  further Intl options, e.g. fraction digits (formatWholeCurrency).
 */
export const formatCurrency = (
    value: number | string | null | undefined,
    code: string = FALLBACK_CURRENCY,
    locale?: string,
    options?: Pick<Intl.NumberFormatOptions, 'minimumFractionDigits' | 'maximumFractionDigits'>
): string => {
    if (value === null || value === undefined || value === '') return '—';
    const n = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(n)) return '—';
    try {
        return new Intl.NumberFormat(locale, { ...options, style: 'currency', currency: code }).format(n);
    } catch {
        // Unknown currency code (non-ISO) — fall back to plain number + code suffix.
        return `${n.toLocaleString(locale, options)} ${code}`;
    }
};

/**
 * Currency in whole units ("$169,910"), for a headline figure where cents only widen it; the
 * exact amount belongs beside it (a tooltip, the copied data). Yen and other zero-decimal
 * currencies are unchanged.
 */
export const formatWholeCurrency = (
    value: number | string | null | undefined,
    code: string = FALLBACK_CURRENCY,
    locale?: string
): string => formatCurrency(value, code, locale, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

/**
 * Compact currency (e.g. "A$1.2M", "¥4,580") via Intl.NumberFormat's
 * built-in compact notation. Supports any ISO 4217 currency.
 */
export const formatCompactCurrency = (
    value: number | string | null | undefined,
    code: string = FALLBACK_CURRENCY,
    locale?: string
): string => {
    if (value === null || value === undefined || value === '') return '—';
    const n = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(n)) return '—';
    try {
        return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: code,
            notation: 'compact',
            maximumFractionDigits: 1,
        }).format(n);
    } catch {
        return `${n.toLocaleString(locale)} ${code}`;
    }
};

export const capitalizeFirstLetter = (str: string | null | undefined): string =>
    typeof str === 'string' && str.length > 0 ? str.charAt(0).toUpperCase() + str.slice(1) : 'N/A';

export const numberWithCommas = (number: number | string, decimalPlaces = 0): string => {
    const fixedNumber = Number(number).toFixed(decimalPlaces);
    return new Intl.NumberFormat(undefined, {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
    }).format(Number(fixedNumber));
};

/**
 * A theta-sketch estimate (reach, unique viewers, overlaps), shown as one: three significant
 * figures and a leading ≈. Reach is good to about ±1 % (±5–7 % on intersections), so
 * "270,472" claims precision the number does not have; "≈270,000" does not.
 * Small counts (under 1,000) stay exact apart from the ≈.
 */
export const formatEstimate = (value: number | null | undefined, { mark = true } = {}): string => {
    if (value === null || value === undefined || !Number.isFinite(value)) return '—';
    if (value === 0) return '0';
    const rounded = Math.abs(value) < 1000 ? Math.round(value) : Number(value.toPrecision(3));
    const text = new Intl.NumberFormat('en-NZ').format(rounded);
    // In a column already headed "Est." a ≈ on every cell is noise: round, and leave it off
    return mark ? `≈${text}` : text;
};
